import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import { DataRoomFileAIAnalysis } from "@/models/data-room/DataRoomFileAIAnalysis";
import { ProcessingState } from "@/models/data-room/ProcessingState";
import { VirtualDataRoomFile } from "@/models/virtual-data-room/VirtualDataRoomFile";
import { CalendarTodayOutlined, HelpOutline } from "@mui/icons-material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { SxProps, styled } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileIcon } from "../utils/FileIcon";
import { FolderNotch } from "../utils/icons/FolderNotch";
import ListIcon from "../utils/icons/ListIcon";
import { SharedFinancialReportGenerationComm } from "../virtual-room-components/SharedFinancialReportGenerationComm";
import { getSuggestedFolderValue } from "./utils";

const debouncer = new Debouncer(100);

export default function DataRoomAISummary({
  file,
  setFile,
  disableEdition,
  disableSuggestedFolder,
  updateFn,
}: {
  file: DataRoomFile | VirtualDataRoomFile;
  setFile: (u: DataRoomFile | VirtualDataRoomFile) => void;
  updateFn: (fileExternalId: string, data: DataRoomFileAIAnalysis) => Promise<ApiResponseV2<DataRoomFile | VirtualDataRoomFile>>;
  disableEdition?: boolean;
  disableSuggestedFolder?: boolean;
}) {
  const { t } = useTranslation("virtualDataRooms");
  switch (file.processingState) {
    case ProcessingState.PROCESSED_SUGGESTED_FOLDER:
    case ProcessingState.PROCESSING_SUGGESTED_FOLDER:
    case ProcessingState.NOT_PROCESSED: {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
          {file?.["type"]! ? (
            <SharedFinancialReportGenerationComm title={t("filePage.buttons.aiAnalysis")} type={"file"} />
          ) : (
            <>
              <Typography>{t("aiSummary.processing.queuedForAnalysis")}</Typography>
            </>
          )}
        </Box>
      );
    }
    case ProcessingState.PROCESSING_AI_SUMMARY: {
      console.log("filesss", file);
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
          {file?.["type"]! ? (
            <SharedFinancialReportGenerationComm title={t("filePage.buttons.aiAnalysis")} type={"file"} />
          ) : (
            <>
              <Typography>{t("aiSummary.processing.beingAnalyzed")}</Typography>
              <Spinner size="4rem" sx={{ color: Colors.LAZO_PURPLE }} />
            </>
          )}
        </Box>
      );
    }
    default:
      break;
  }

  function handleKeyDown(ev: React.KeyboardEvent<HTMLDivElement>) {
    if (ev.key === "Enter") {
      ev.preventDefault();
      onSubmit(form);
      setEnabledField("");
    } else if (ev.key === "Escape") {
      setEnabledField("");
    }
  }

  function onSubmit(data: DataRoomFileAIAnalysis) {
    debouncer.executeLastCall(() => {
      const body = { ...file.aiAnalysis, ...data };
      updateFn(file.externalId, body).then((r) => {
        r.onSuccess((u) => {
          setFile(u);
          setForm(u.aiAnalysis);
        });
      });
    });
  }

  const [enabledField, setEnabledField] = useState<string>("");
  function handleFieldClick(ev: React.MouseEvent<HTMLSpanElement, MouseEvent>) {
    if (disableEdition) return;
    setEnabledField(ev.currentTarget.id);
  }

  const currentFieldRef = useRef<any>(null);

  function submit() {
    onSubmit(form);
    setEnabledField("");
  }

  const [newProvision, setNewProvision] = useState<string>("");
  function addNewProvision() {
    if (newProvision) {
      let clone = [...form.keyProvisions];
      clone.push(newProvision);
      setForm((prev) => ({ ...prev, keyProvisions: clone }));
      onSubmit({ ...file.aiAnalysis, keyProvisions: clone });
      setNewProvision("");
    }
    setEnabledField("");
  }

  const [form, setForm] = useState<DataRoomFileAIAnalysis>({
    ...(file.aiAnalysis ? file.aiAnalysis : {}),
  });

  function updatePartiesInvolved(field: string, value: string, idx: number) {
    let clone = [...form.partiesInvolved];
    clone[idx][field] = value;
    setForm((prev) => ({ ...prev, partiesInvolved: clone }));
  }

  function updateProvisions(value: string, idx: number) {
    let clone = [...form.keyProvisions];
    clone[idx] = value;
    setForm((prev) => ({ ...prev, keyProvisions: clone }));
  }

  function addParty() {
    if (disableEdition) return;
    let clone = structuredClone(form);
    clone.partiesInvolved.push({ by: "New", title: "", email: "" });
    onSubmit(clone);
  }

  const hasKeyProvisions = file.aiAnalysis?.keyProvisions?.length > 0;

  return (
    <>
      <SummaryDataContainer>
        <SummaryDataRow>
          <Typography flex="1" className="section-title">
            <FileIcon sx={{ fill: Colors.BLUE_4763F4 }} />
            {t("aiSummary.sections.documentTitle")}
          </Typography>
          {enabledField === "documentTitle" ? (
            <>
              <TextField
                onChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    documentTitle: v.target.value,
                  }))
                }
                onKeyDown={handleKeyDown}
                multiline
                maxRows={3}
                inputRef={currentFieldRef}
                autoFocus
                value={form.documentTitle}
                sx={{ ...FieldSx }}
                onBlur={submit}
              />
            </>
          ) : (
            <Typography id="documentTitle" onClick={handleFieldClick} flex="1" className={disableEdition ? "" : "editable-field"}>
              {file.aiAnalysis?.documentTitle || "-"}
            </Typography>
          )}
        </SummaryDataRow>
        {!disableSuggestedFolder && (
          <SummaryDataRow>
            <Typography flex="1" className="section-title">
              <FolderNotch sx={{ fill: Colors.BLUE_4763F4 }} />
              {t("aiSummary.sections.suggestedFolder")}
            </Typography>
            <Typography flex="1">{getSuggestedFolderValue(file as any, true)}</Typography>
          </SummaryDataRow>
        )}
        <SummaryDataRow>
          <Typography flex="1" className="section-title">
            <HelpOutline sx={{ fill: Colors.BLUE_4763F4 }} />
            {t("aiSummary.sections.description")}
          </Typography>
          {enabledField === "description" ? (
            <>
              <TextField
                onBlur={submit}
                onKeyDown={handleKeyDown}
                onChange={(v) => setForm((prev) => ({ ...prev, description: v.target.value }))}
                multiline
                autoFocus
                maxRows={3}
                inputRef={currentFieldRef}
                value={form.description}
                sx={{ ...FieldSx }}
              />
            </>
          ) : (
            <Typography id="description" flex="1" onClick={handleFieldClick} className={disableEdition ? "" : "editable-field"}>
              {file.aiAnalysis?.description || "-"}
            </Typography>
          )}
        </SummaryDataRow>
        <SummaryDataRow>
          <Typography flex="1" className="section-title">
            <HelpOutline sx={{ fill: Colors.BLUE_4763F4 }} />
            {t("aiSummary.sections.namedEntities")}
          </Typography>
          <Box
            flex="1"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.875rem",
              maxHeight: "8rem",
              overflow: "auto",
            }}
          >
            {file.aiAnalysis?.partiesInvolved?.map((pi, idx) => (
              <>
                <Box key={idx} display="flex" flexDirection="column" alignItems="flex-start">
                  <Box display="flex" gap="0.5rem" alignItems="center">
                    <Typography className="section-title">{t("aiSummary.fields.by")}</Typography>
                    {enabledField === `by-${idx}` ? (
                      <TextField
                        onKeyDown={handleKeyDown}
                        onBlur={submit}
                        onChange={(v) => updatePartiesInvolved("by", v.target.value, idx)}
                        autoFocus
                        inputRef={currentFieldRef}
                        value={form.partiesInvolved[idx].by}
                        sx={{ ...FieldSx }}
                      />
                    ) : (
                      <Typography id={`by-${idx}`} className={disableEdition ? "" : "editable-field"} onClick={handleFieldClick}>
                        {pi.by || "-"}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" gap="0.5rem" alignItems="center">
                    <Typography className="section-title">{t("aiSummary.fields.title")}</Typography>
                    {enabledField === `title-${idx}` ? (
                      <TextField
                        onKeyDown={handleKeyDown}
                        onChange={(v) => updatePartiesInvolved("title", v.target.value, idx)}
                        autoFocus
                        onBlur={submit}
                        inputRef={currentFieldRef}
                        value={form.partiesInvolved[idx].title}
                        sx={{ ...FieldSx }}
                      />
                    ) : (
                      <Typography
                        width={"100%"}
                        id={`title-${idx}`}
                        className={disableEdition ? "" : "editable-field"}
                        onClick={handleFieldClick}
                      >
                        {pi.title || "-"}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" gap="0.5rem" alignItems="center">
                    <Typography className="section-title">{t("aiSummary.fields.email")}</Typography>
                    {enabledField === `email-${idx}` ? (
                      <TextField
                        onKeyDown={handleKeyDown}
                        onChange={(v) => updatePartiesInvolved("email", v.target.value, idx)}
                        autoFocus
                        onBlur={submit}
                        inputRef={currentFieldRef}
                        value={form.partiesInvolved[idx].email}
                        sx={{ ...FieldSx }}
                      />
                    ) : (
                      <Typography id={`email-${idx}`} className={disableEdition ? "" : "editable-field"} onClick={handleFieldClick}>
                        {pi.email || "-"}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            ))}
            {!disableEdition && (
              <Typography
                id={`party-new`}
                fontSize="0.75rem"
                fontWeight="500"
                sx={{
                  transition: "background-color 0.2s ease-in",
                  borderRadius: "0.25rem",
                  color: Colors.BLUE_4763F4,
                  "&:hover": {
                    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
                    cursor: "pointer",
                  },
                }}
                onClick={addParty}
              >
                {t("aiSummary.actions.addParty")}
              </Typography>
            )}
          </Box>
        </SummaryDataRow>
        <SummaryDataRow>
          <Typography flex="1" className="section-title">
            <CalendarTodayOutlined sx={{ fill: Colors.BLUE_4763F4 }} />
            {t("aiSummary.sections.date")}
          </Typography>
          {enabledField === "dateOfSignature" ? (
            <>
              <TextField
                onKeyDown={handleKeyDown}
                autoFocus
                onBlur={submit}
                inputRef={currentFieldRef}
                value={form.dateOfSignature}
                onChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    dateOfSignature: v.target.value,
                  }))
                }
                sx={{ ...FieldSx }}
              />
            </>
          ) : (
            <Typography id="dateOfSignature" flex="1" onClick={handleFieldClick} className={disableEdition ? "" : "editable-field"}>
              {file.aiAnalysis?.dateOfSignature || "-"}
            </Typography>
          )}
        </SummaryDataRow>
        <div
          style={{
            height: "1px",
            minHeight: "1px",
            backgroundColor: Colors.LIGHT_GRAY_DEDEE1,
            width: "100%",
          }}
        />
        <Box display="flex" flexDirection="column" justifyContent="center" gap="0.75rem">
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              fontWeight: "600",
              fontSize: "0.75rem",
            }}
          >
            <ListIcon fillColor={Colors.BLUE_4763F4} />
            {t("aiSummary.sections.keyProvisions")}
          </Typography>
          {!hasKeyProvisions && (
            <Typography fontSize="0.75rem" fontWeight="500">
              {t("aiSummary.messages.noProvisions")}
            </Typography>
          )}
          {hasKeyProvisions &&
            file.aiAnalysis.keyProvisions.map((kp, idx) =>
              enabledField === `keyProv-${idx}` ? (
                <TextField
                  onBlur={submit}
                  autoFocus
                  multiline
                  onKeyDown={handleKeyDown}
                  maxRows={3}
                  inputRef={currentFieldRef}
                  value={form.keyProvisions[idx]}
                  onChange={(v) => updateProvisions(v.target.value, idx)}
                  sx={{ ...FieldSx }}
                />
              ) : (
                <Typography
                  id={`keyProv-${idx}`}
                  fontSize="0.75rem"
                  fontWeight="500"
                  sx={{
                    transition: "background-color 0.2s ease-in",
                    borderRadius: "0.25rem",
                    ...(!disableEdition && {
                      "&:hover": {
                        backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
                        cursor: "pointer",
                      },
                    }),
                  }}
                  onClick={handleFieldClick}
                >
                  • {kp}
                </Typography>
              )
            )}
          {enabledField === `keyProv-new` ? (
            <TextField
              onBlur={addNewProvision}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") addNewProvision();
                else if (ev.key === "Escape") setEnabledField("");
              }}
              autoFocus
              multiline
              maxRows={3}
              inputRef={currentFieldRef}
              value={newProvision}
              onChange={(v) => setNewProvision(v.target.value)}
              sx={{ ...FieldSx }}
            />
          ) : (
            !disableEdition && (
              <Typography
                id={`keyProv-new`}
                fontSize="0.75rem"
                fontWeight="500"
                sx={{
                  transition: "background-color 0.2s ease-in",
                  borderRadius: "0.25rem",
                  color: Colors.BLUE_4763F4,
                  "&:hover": {
                    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
                    cursor: "pointer",
                  },
                }}
                onClick={handleFieldClick}
              >
                {t("aiSummary.actions.addProvision")}
              </Typography>
            )
          )}
        </Box>
      </SummaryDataContainer>
    </>
  );
}

const SummaryDataContainer = styled(Box)({
  display: "flex",
  gap: "1rem",
  height: "100%",
  width: "100%",
  overflowY: "auto",
  flexDirection: "column",
});

const SummaryDataRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  "& .MuiTypography-root": {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
    fontWeight: "400",
    fontSize: "0.75rem",
    padding: "0 0.25rem",
    "&.section-title": {
      fontWeight: "600",
      fontSize: "0.75rem",
    },
    "&.editable-field": {
      transition: "background-color 0.2s ease-in",
      borderRadius: "0.25rem",
      "&:hover": {
        backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
        cursor: "pointer",
      },
    },
  },
});

const FieldSx: SxProps = {
  flex: "1",
  marginRight: "0.25rem",
  boxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.15)",
  maxWidth: "100%",
  height: "fit-content",
  backgroundColor: "white",
  "&.MuiTextField-root": {
    borderRadius: "0.5rem",
    "&  .MuiOutlinedInput-root": {
      height: "fit-content",
      padding: "0.25rem 0.25rem",
      "&.Mui-focused fieldset": {
        border: 0,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: 0,
      },
      "& .MuiOutlinedInput-input": {
        fontSize: "0.75rem",
        fontWeight: "400",
        padding: "0.25rem 0.25rem",
      },
      "&.MuiOutlinedInput-error": {
        color: Colors.RED,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: Colors.RED,
        },
      },
    },
  },
};
